import { clientEnv } from '@/env/client';

export type RetentionEventName =
  | 'workbench_view'
  | 'prompt_engaged'
  | 'landing_generate_intent'
  | 'landing_login_offer_view'
  | 'landing_login_offer_action'
  | 'landing_auth_return'
  | 'image_generate_intent'
  | 'image_login_offer_view'
  | 'image_login_offer_action'
  | 'image_auth_return'
  | 'image_credits_required_view'
  | 'image_credits_required_action'
  | 'image_generation_submit'
  | 'image_generation_failure'
  | 'pricing_view'
  | 'begin_checkout'
  | 'checkout_start_failed'
  | 'checkout_return'
  | 'purchase'
  | 'daily_check_in_view'
  | 'daily_check_in_auth_start'
  | 'daily_check_in_claim'
  | 'daily_check_in_reward_copy'
  | 'history_continue'
  | 'variant_create'
  | 'retry_failed_generation'
  | 'topup_offer_view'
  | 'topup_checkout_start'
  | 'checkout_return_offer_shown'
  | 'checkout_return_offer_declined'
  | 'checkout_return_offer_accepted'
  | 'checkout_return_offer_checkout_started'
  | 'google_one_tap_prompt_request'
  | 'google_one_tap_prompt_outcome'
  | 'google_one_tap_credential'
  | 'google_one_tap_success'
  | 'google_one_tap_failure'
  | 'google_oauth_start'
  | 'google_oauth_slow'
  | 'google_oauth_redirect'
  | 'google_oauth_failure'
  | 'video_config_exceeds_free_credits'
  | 'video_free_preset_apply'
  | 'video_generate_intent'
  | 'video_login_offer_view'
  | 'video_login_offer_action'
  | 'video_auth_return'
  | 'video_credits_required_view'
  | 'video_credits_required_action'
  | 'video_generation_submit'
  | 'video_generation_success'
  | 'video_generation_failure'
  | 'video_editor_open'
  | 'video_editor_submit'
  | 'video_editor_success'
  | 'video_editor_failure'
  | 'template_video_submit'
  | 'template_video_success'
  | 'template_video_failure'
  | 'video_download_click'
  | 'video_download_gate_view'
  | 'video_download_gate_dismiss'
  | 'video_download_plans_view'
  | 'video_download_checkout_start'
  | 'video_single_download_offer_view'
  | 'video_quick_download_offer_view'
  | 'video_quick_download_offer_action'
  | 'video_single_download_checkout_start'
  | 'video_download_start'
  | 'image_generation_success'
  | 'image_download_click'
  | 'image_download_gate_view'
  | 'image_download_gate_dismiss'
  | 'image_download_checkout_start'
  | 'image_single_download_offer_view'
  | 'image_single_download_checkout_start'
  | 'image_download_start';

export type GenerationFailureCategory =
  | 'content_policy'
  | 'insufficient_credits'
  | 'provider_timeout'
  | 'provider_error'
  | 'rate_limit'
  | 'network_error'
  | 'r2_error'
  | 'unknown';

export function getBalanceBucket(balance?: number) {
  if (typeof balance !== 'number' || !Number.isFinite(balance))
    return 'unknown';
  if (balance <= 0) return '0';
  if (balance <= 10) return '1-10';
  if (balance <= 30) return '11-30';
  if (balance <= 100) return '31-100';
  return '101+';
}

export function classifyGenerationFailure(
  message: string | undefined,
  category?: string | null
): GenerationFailureCategory {
  const knownCategories = new Set<GenerationFailureCategory>([
    'content_policy',
    'insufficient_credits',
    'provider_timeout',
    'provider_error',
    'rate_limit',
    'network_error',
    'r2_error',
    'unknown',
  ]);
  if (category && knownCategories.has(category as GenerationFailureCategory)) {
    return category as GenerationFailureCategory;
  }

  const normalized = message?.toLowerCase() ?? '';
  if (/content|policy|moderation|safety/.test(normalized)) {
    return 'content_policy';
  }
  if (/credit|balance/.test(normalized)) return 'insufficient_credits';
  if (/rate.?limit|too many requests|429/.test(normalized)) return 'rate_limit';
  if (/timeout|timed out|still running/.test(normalized)) {
    return 'provider_timeout';
  }
  if (/network|fetch|connection|offline/.test(normalized)) {
    return 'network_error';
  }
  if (/r2|upload|storage|save/.test(normalized)) return 'r2_error';
  if (normalized) return 'provider_error';
  return 'unknown';
}

export function classifyCheckoutFailure(error: unknown) {
  const message = error instanceof Error ? error.message.toLowerCase() : '';
  if (/auth|sign.?in|unauthor|session/.test(message)) return 'auth';
  if (/network|fetch|connection|offline/.test(message)) return 'network';
  if (/config|disabled|required|not found/.test(message)) return 'config';
  if (/provider|stripe|creem|checkout/.test(message)) return 'provider';
  return 'unknown';
}

export function trackRetentionEvent(
  name: RetentionEventName,
  params: Record<string, string | number | boolean | undefined> = {}
) {
  if (typeof window === 'undefined') return;
  const googleAnalyticsId = clientEnv.VITE_GOOGLE_ANALYTICS_ID;
  if (import.meta.env.PROD && !googleAnalyticsId) return;
  const analyticsWindow = window as typeof window & {
    gtag?: (
      command: 'event',
      eventName: string,
      eventParams?: Record<string, unknown>
    ) => void;
    clarity?: {
      (command: 'event', eventName: string): void;
      (command: 'set', key: string, value: string): void;
    };
  };
  analyticsWindow.gtag?.('event', name, {
    ...params,
    ...(googleAnalyticsId ? { send_to: googleAnalyticsId } : {}),
  });
  analyticsWindow.clarity?.('event', name);

  for (const key of [
    'workflow',
    'entrySurface',
    'source',
    'errorCategory',
    'dropoffReason',
    'funnelStage',
  ]) {
    const value = params[key];
    if (value === undefined) continue;
    analyticsWindow.clarity?.('set', key, String(value));
  }
}
